// Spreadsheet safety (02 §8).
//
// Business names in this system are SCRAPED. They come from Google, from directories, from
// whatever a business chose to call itself — which makes them attacker-controlled input that lands
// in the owner's spreadsheet. Excel and LibreOffice treat a cell starting with `=`, `+`, `-`, `@`,
// tab or CR as a formula, and a formula in a spreadsheet can call out to a URL or, with a click
// through one dialog, run a command.
//
// So every exported cell goes through here. The apostrophe prefix is what Excel understands as
// "this is text"; XLSX writers additionally get an explicit text cell type, because the prefix
// alone is a convention and the type is a guarantee.

const DANGEROUS = /^[=+\-@\t\r]/;

export function safeCell(value: unknown): string {
  if (value === null || value === undefined) return '';
  const text = String(value);
  return DANGEROUS.test(text) ? `'${text}` : text;
}

/** One CSV row, quoted per RFC 4180 after neutralisation. */
export function csvRow(values: readonly unknown[]): string {
  return values
    .map((value) => {
      const cell = safeCell(value);
      // \r as well as \n: a carriage return inside an unquoted cell ends the row in Excel.
      return /["\r\n,;]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;
    })
    .join(',');
}

/**
 * ASCII filenames (02 §8). A Cyrillic filename survives most systems and then breaks on the one
 * that matters — an e-mail attachment, a Windows share, someone's phone.
 */
const TRANSLIT: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'sht',
  ъ: 'a',
  ь: 'y',
  ю: 'yu',
  я: 'ya',
};

export function asciiFilename(name: string, extension: string): string {
  const base = [...name.toLowerCase()]
    .map((char) => TRANSLIT[char] ?? char)
    .join('')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);
  return `${base === '' ? 'leadg' : base}.${extension}`;
}

/** Hands the browser a file. Revoking on the next tick keeps a long session from leaking blobs. */
export function download(filename: string, mime: string, content: string): void {
  const blob = new Blob([content], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  setTimeout(() => URL.revokeObjectURL(url), 0);
}
